import type { ChatSession, Document, Message, User } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
}

class ApiClient {
  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', body, headers = {} } = options;

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'An error occurred' }));
      throw new Error(error.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Chat endpoints
  async createChat(): Promise<ChatSession> {
    return this.request<ChatSession>('/chat', { method: 'POST' });
  }

  async getChats(): Promise<ChatSession[]> {
    return this.request<ChatSession[]>('/chat');
  }

  async getChat(id: string): Promise<ChatSession> {
    return this.request<ChatSession>(`/chat/${id}`);
  }

  async sendMessage(chatId: string, content: string, effort: string): Promise<Message> {
    return this.request<Message>(`/chat/${chatId}/messages`, {
      method: 'POST',
      body: { content, effortLevel: effort },
    });
  }

  // Send message and get streaming response as plain text
  async sendMessageStream(chatId: string, content: string, effort: string): Promise<string> {
    const response = await fetch(`${API_BASE_URL}/chat/${chatId}/messages/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content, effortLevel: effort }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to stream message' }));
      throw new Error(error.message || `HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let fullResponse = '';
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.token) {
              fullResponse += parsed.token;
            }
          } catch {
            // Try parsing as raw text
            if (line.length > 5) {
              fullResponse += line;
            }
          }
        } else if (line.trim().length > 0) {
          // Raw text chunk
          fullResponse += line;
        }
      }
    }

    return fullResponse;
  }

  // Document endpoints
  async uploadDocument(file: File): Promise<Document> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/documents`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to upload document');
    }

    return response.json();
  }

  async getDocuments(): Promise<Document[]> {
    return this.request<Document[]>('/documents');
  }

  async deleteDocument(id: string): Promise<void> {
    return this.request<void>(`/documents/${id}`, { method: 'DELETE' });
  }

  // User endpoints
  async getUser(): Promise<User> {
    return this.request<User>('/user');
  }

  async getTokenBalance(): Promise<{ balance: number }> {
    return this.request<{ balance: number }>('/user/tokens');
  }

  // Subscription endpoints
  async getSubscription(): Promise<{
    tier: string;
    status: string;
    tokensRemaining: number;
    tokensPerMonth: number;
  }> {
    return this.request('/subscription');
  }

  async purchaseTokens(packId: string): Promise<{ success: boolean; newBalance: number }> {
    return this.request('/tokens/purchase', {
      method: 'POST',
      body: { packId },
    });
  }

  async redeemGiftCard(code: string): Promise<{ success: boolean; tokensAdded: number }> {
    return this.request('/gift-cards/redeem', {
      method: 'POST',
      body: { code },
    });
  }
}

export const api = new ApiClient();
